import authServices from './auth'
import userServices from './user'
import coreServices from './core'
import syncServices from './sync'
import workspaceServices from './workspace'
import projectServices from './project'
import secretServices from './secret'
import environmentServices from './environment'

import storeActions from '../store/actions'
import global from '../config/global'
import { sortSecrets, sortEnvironments } from '../utils/common'

import { CipherType } from '../core-js/src/enums'

async function update_auth_info(data, isUnlock = true) {
  authServices.update_access_token(data.token)
  authServices.update_access_token_type(data.token_type)
  userServices.update_account_info({
    username: data.username,
    full_name: data.full_name,
  })
  if (isUnlock) {
    await coreServices.unlock(data)
  }
}

async function sync_profile() {
  const { profile } = await syncServices.sync_profile_data();
  const prelogin = await userServices.get_prelogin();
  const accountInfo = await userServices.get_account();
  global.store.dispatch(storeActions.updateUserInfo({
    ...accountInfo,
    ...prelogin,
    ...profile
  }));
  await global.jsCore.syncService.syncProfile({
    key: profile?.key,
    privateKey: profile?.privateKey,
    organizations: profile?.projects,
    securityStamp: profile?.securityStamp || null,
    emailVerified: profile?.emailVerified || false
  })
}

const syncingEnvironmentsProjects = new Set();
const syncingSecretsProjects = new Set();

async function sync_project_environments(project) {
  if (!project?.id) return;
  if (syncingEnvironmentsProjects.has(project.id)) {
    return;
  }
  syncingEnvironmentsProjects.add(project.id);
  try {
    global.store.dispatch(storeActions.updateIsFetchProjectEnvironments(true));
    const total = project.total_environment || 0;
    if (total <= 0) {
      global.store.dispatch(storeActions.updateProjectEnvironments([]));
      return;
    }

    const size = 5000;
    const totalPages = Math.ceil(total / size);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    const pagePromises = pages.map(async (page) => {
      try {
        const response = await environmentServices.list(project.id, { page, size });
        if (!response?.results?.length) return [];
        const itemMap = new Map(response.results.map((r) => [String(r.id), r]));
        const results = await coreServices.decrypt_ciphers(response.results, CipherType.Environment);
        return results.map((d) => {
          const item = itemMap.get(String(d.id)) || {};
          return {
            id: d.id,
            name: d.environment.name,
            hash: item.hash,
            creationDate: d.creationDate,
            updatedDate: d.updatedDate,
            revisionDate: d.revisionDate,
          };
        });
      } catch (error) {
        console.error(`Error syncing environments page ${page}:`, error);
        return [];
      }
    });

    const pagesData = await Promise.all(pagePromises);
    const environments = sortEnvironments(pagesData.flat());
    global.store.dispatch(storeActions.updateProjectEnvironments(environments));
  } finally {
    syncingEnvironmentsProjects.delete(project.id);
    global.store.dispatch(storeActions.updateIsFetchProjectEnvironments(false));
  }
}

async function sync_project_secrets(project) {
  if (!project?.id) return;
  if (syncingSecretsProjects.has(project.id)) {
    return;
  }
  syncingSecretsProjects.add(project.id);
  try {
    global.store.dispatch(storeActions.updateIsFetchProjectSecrets(true));
    const total = project.total_secret || 0;
    if (total <= 0) {
      global.store.dispatch(storeActions.updateProjectSecrets([]));
      return;
    }

    const size = 5000;
    const totalPages = Math.ceil(total / size);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    const pagePromises = pages.map(async (page) => {
      try {
        const response = await secretServices.list(project.id, { page, size });
        if (!response?.results?.length) return [];
        const itemMap = new Map(response.results.map((r) => [String(r.id), r]));
        const results = await coreServices.decrypt_ciphers(response.results, CipherType.Secret);
        return results.map((d) => {
          const item = itemMap.get(String(d.id)) || {};
          return {
            id: d.id,
            key: d.secret.key,
            name: d.secret.key,
            hash: item.secret_hash,
            environmentId: item.environment_id,
            creationDate: d.creationDate,
            updatedDate: d.updatedDate,
            revisionDate: d.revisionDate,
            expiredDate: d.expiredDate,
          };
        });
      } catch (error) {
        console.error(`Error syncing secrets page ${page}:`, error);
        return [];
      }
    });

    const pagesData = await Promise.all(pagePromises);
    const secrets = sortSecrets(pagesData.flat());
    global.store.dispatch(storeActions.updateProjectSecrets(secrets));
  } finally {
    syncingSecretsProjects.delete(project.id);
    global.store.dispatch(storeActions.updateIsFetchProjectSecrets(false));
  }
}

async function fetch_data(returnRouter = null) {
  const requests = [
    workspaceServices.list({ paging: 0 }),
    userServices.factor2(),
  ]

  await Promise.all(requests).then(async ([
    workspaces,
    factor2,
  ]) => {
    global.store.dispatch(storeActions.updateWorkspaces(workspaces))
    global.store.dispatch(storeActions.updateFactor2(factor2));

    const workspaceId = returnRouter?.params?.workspace_id || null;
    const projectId = returnRouter?.params?.project_id || null;
    const targetWs = (workspaceId && workspaces.find((w) => w.id == workspaceId)) || workspaces[0];

    if (targetWs) {
      global.store.dispatch(storeActions.updateSelectedWorkspace(targetWs));
      const projects = await projectServices.list({ paging: 0, organization_id: targetWs.id });
      global.store.dispatch(storeActions.updateProjects(projects));

      if (returnRouter && returnRouter.name !== 'SPLASH') {
        if (projectId && !projects.find((p) => p.id == projectId)) {
          global.navigate("PROJECTS", { workspace_id: targetWs.id });
        } else {
          global.navigate(returnRouter.name, returnRouter.params, returnRouter.query);
        }
      } else {
        global.navigate("PROJECTS", { workspace_id: targetWs.id });
      }
    } else {
      if (returnRouter) {
        global.navigate(returnRouter.name, returnRouter.params, returnRouter.query);
      }
    }
  })
}

export default {
  update_auth_info,
  sync_profile,
  fetch_data,
  sync_project_secrets,
  sync_project_environments
}